import React from "react"
import Tooltip from "design_system/core/components/tooltip"
import defaultAvatar from "images/avatar/smiley/avatar@2x.png"
import isEmpty from "lodash/isEmpty"
import ProfileCurrentLevel from "@love-irish/community-profile-current-level"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuPortal,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@love-irish/core-components/ui/dropdown-menu"

import { Button } from "@love-irish/core-components/ui/button"
import {
  IconBackpack,
  IconBell,
  IconCreditCard,
  IconDashboard,
  IconFileTypeDoc,
  IconHelp,
  IconHome,
  IconKeyboard,
  IconLogout,
  IconMail,
  IconSettings,
  IconShield,
  IconUser,
} from "@tabler/icons-react"
import routes from "@love-irish/routes"
import formatter from "design_system/utils/formatter"

const SettingsLink = () => (
  <a href="/settings" className="inline-block">
    <span className="align-middle">Settings</span>
  </a>
)

const ProfileLink = ({ username }) => (
  <a
    href={username ? `/@${username}` : "/settings/username"}
    className="inline-block"
  >
    <span className="align-middle">Profile</span>
  </a>
)

const LoginLink = ({ username }) => (
  <a href={routes.loginPath()} className="flex gap-1.5">
    <span className="align-middle">Login</span>
  </a>
)

const MenuLink = ({ label, icon, href }) => (
  <a href={href} className="flex gap-1.5 items-center grow">
    {icon}
    <span className="align-middle">{label}</span>
  </a>
)

const LearnMore = ({ username }) => (
  <a href={"/about-us"} className="inline-block">
    <span className="align-middle">Learn more</span>
  </a>
)

const CreateAccountLink = ({ username }) => (
  <a href={"/signup"} className="inline-block">
    <span className="align-middle">Signup</span>
  </a>
)
const CircularProgress = ({ value, size = 20, strokeWidth = 3 }) => {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference * (1 - value / 100)

  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        className="stroke-muted"
        strokeWidth={strokeWidth}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        className="stroke-primary"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
      />
    </svg>
  )
}

const CreditSection = () => {
  const totalCredits = 10_000
  const creditsUsed = 8_978
  const percentLeft = (creditsUsed / totalCredits) * 100
  return (
    <div className="bg-gray-alpha-50 dark:bg-transparent">
      <div className="bg-background dark:bg-gray-alpha-100 p-1 w-full text-sm stack gap-3.5 shadow-natural-xs rounded-lg">
        <div className="flex justify-between text-gray-alpha-500 mb-2">
          <div className="flex items-center">
            <div>
              <CircularProgress value={percentLeft} size={20} strokeWidth={3} />
            </div>
            <div className="text-foreground font-medium ml-1.5">Balance</div>
          </div>
          <div>
            <Button href={routes.pricingPath()}>Upgrade</Button>
          </div>
        </div>
        <div className="flex text-xm items-center gap-2.5">
          <div className="stack gap-0.5 flex-grow">
            <div className="flex justify-between">
              <div className="text-subtle">Total</div>
              <div className="tabular-nums font-medium">
                {formatter.number(totalCredits)} credits
              </div>
            </div>
            <div className="flex justify-between">
              <div className="text-subtle">Remaining</div>
              <div className="tabular-nums font-medium">
                {formatter.number(creditsUsed)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const LoggedOutWelcomeMessage = () => (
  <div className="px-1">
    Welcome{" "}
    <Tooltip
      className="text-center"
      title="You just learned &rdquo;my friend&ldquo; in Irish! ☘️"
      placement="bottom"
    >
      <strong className="font-medium text-black dark:text-white text-definition">
        a chara
      </strong>
    </Tooltip>
  </div>
)
const LoggedInWelcomeMessage = () => <div className="px-1">Welcome back!</div>

const UserAvatar = ({ currentUser }) => {
  const loggedOut = isEmpty(currentUser)
  return (
    <div className="sm:ml-6 items-center flex">
      <div className="text-right hidden sm:block">
        {loggedOut ? <LoggedOutWelcomeMessage /> : <LoggedInWelcomeMessage />}
        {loggedOut ? (
          <div className="px-1 text-xs mt-0.5 flex gap-2">
            <LoginLink /> &middot; <CreateAccountLink />
          </div>
        ) : (
          <div className="px-1 text-xs mt-0.5 flex gap-2">
            <ProfileCurrentLevel progress={currentUser.levelProgress} />
            <span>&middot;</span>
            <SettingsLink />
          </div>
        )}
      </div>

      <div className="relative ml-3 flex items-center" id="user-menu">
        {loggedOut ? (
          <img
            className="h-8 w-8 rounded-full"
            src={defaultAvatar}
            alt="Default avatar smiley face"
          />
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger className="focus:outline-none">
              {currentUser?.profileIconUrl ? (
                <img
                  className="w-[32px] pixel-art cursor-pointer"
                  src={currentUser?.profileIconUrl}
                  alt="Avatar for current user"
                />
              ) : (
                <img
                  className="h-8 w-8 rounded-full cursor-pointer"
                  src={defaultAvatar}
                  alt="Default avatar smiley face"
                />
              )}
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-72" align="end">
              <DropdownMenuGroup className="hidden">
                <DropdownMenuLabel>Credits</DropdownMenuLabel>
                <CreditSection />
              </DropdownMenuGroup>
              <DropdownMenuSeparator className="hidden" />
              <DropdownMenuGroup>
                <DropdownMenuLabel>Account</DropdownMenuLabel>
                <DropdownMenuItem>
                  <MenuLink
                    label="Profile"
                    icon={<IconUser />}
                    href={
                      currentUser?.slug
                        ? `/@${currentUser?.slug}`
                        : "/settings/username"
                    }
                  />
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <MenuLink
                    label="Dashboard"
                    icon={<IconHome />}
                    href={routes.dashboardPath()}
                  />
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <MenuLink
                    label="Inventory"
                    icon={<IconBackpack />}
                    href={routes.inventoryPath()}
                  />
                </DropdownMenuItem>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="cursor-pointer">
                    <IconSettings />
                    Settings
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                      <DropdownMenuGroup>
                        <DropdownMenuLabel>Preferences</DropdownMenuLabel>
                        <DropdownMenuItem>
                          <IconBell />
                          Notifications
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <IconShield />
                          Privacy & Security
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup>
                        <DropdownMenuItem>
                          <IconSettings />
                          All settings
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem>
                  <IconHelp />
                  Help & Support
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <IconFileTypeDoc />
                  Documentation
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem variant="destructive">
                  <MenuLink
                    label="Logout"
                    icon={<IconLogout />}
                    href={routes.logoutPath()}
                  />
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  )
}

export default UserAvatar
